import React, { useEffect, createContext } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Category, ProductApi, Shop, OrderObject } from 'common/types';
import { ReduxState, LoadingData } from 'services/types';
import { RouteComponentProps } from 'react-router-dom';
import RentalView from './RentalView';
import Spinner from 'components/Spinner';
import Container from 'components/Container';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'services/localization/useTranslation';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import * as RentalViewActions from 'actions/RentalViewActions';
import * as NotificationActions from 'actions/NotificationActions';

interface StateToProps {
	activeStoreRental: LoadingData<OrderObject> | null;
	stockProducts: LoadingData<ProductApi[]>;
	categories: LoadingData<Category[]>;
	activeShop: Shop;
}

type Props = StateToProps & RouteComponentProps<{ bookingId: string }>;

export const ActionFeedbackContext = createContext({
	rentAgainCreated: () => {},
});

export const RentalViewContainer = ({ ...props }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const bookingId = props.match ? props.match.params.bookingId : 'no id';
	const { activeStoreRental, stockProducts, categories, activeShop } = props;

	useEffect(() => {
		getActiveRental(bookingId);
		return () => {
			dispatch(RentalViewActions.removeActiveOrderListeners());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bookingId]);

	const getActiveRental = (activeBookingId: string) => {
		if (!activeShop) {
			return;
		}
		dispatch(
			RentalViewActions.addActiveOrderListeners({
				orderId: activeBookingId,
				shopId: activeShop.shopInfo.id,
			}),
		);
	};

	const rentAgainCreated = () => {
		dispatch(
			NotificationActions.showNotification({
				message: t('rentAgain.newRentalCreated', 'New rental created!'),
				variant: 'info',
			}),
		);
	};

	return (
		<ActionFeedbackContext.Provider value={{ rentAgainCreated }}>
			{!activeStoreRental ||
			activeStoreRental.loading ||
			stockProducts.loading ||
			categories.loading ? (
				<Spinner in={true} />
			) : activeStoreRental.error || stockProducts.error || categories.error ? (
				<Container>
					<Typography>
						{t('errorLoadingRental', 'Error loading rental: ')} {activeStoreRental.error || ''}
					</Typography>
				</Container>
			) : (
				<React.Fragment>
					<RentalView rental={activeStoreRental.data!} />
				</React.Fragment>
			)}
		</ActionFeedbackContext.Provider>
	);
};

const mapStateToProps = (state: ReduxState): StateToProps => {
	return {
		activeStoreRental: RentalViewSelectors._activeOrder(state),
		stockProducts: state.stock.stockProducts,
		categories: state.stock.categories,
		activeShop: state.shop.shop.data!,
	};
};

export default connect(mapStateToProps)(RentalViewContainer);
